/**
 * ExpandSum Component
 *
 * This component renders footer content for the table when the **"Expand Sum"** mode
 * is active. It shows aggregated values for each column, and for the LOCATION column
 * specifically, it provides a currency selector menu to allow switching between
 * different currency summaries.
 *
 * It supports dynamic merging of LOCATION and CCY columns, conditional rendering based
 * on the expand-sum state, and auto-closing of the currency menu via scroll/click-outside
 * behavior.
 *
 * Props:
 *  - `column` (string): The current table column key being rendered.
 *  - `isLastColumn` (boolean): Determines right-edge border behavior for merged cells.
 *  - `isFirstColumnAfterCCY` (boolean): Used to correctly apply borders to the first
 *    column after the CCY column when CCY is merged/hidden.
 *  - `className` (string): Additional CSS classes controlling visibility or layout
 *    (e.g., `"hidden_footer"`).
 *
 * State/Refs:
 *  - `shouldExpandCurrencies` (boolean): Controls whether the currency dropdown
 *    list is visible when expanding the sum inside LOCATION column.
 *
 *  - `data`: The aggregated footer value for the current column, returned by
 *    `useViewPerCurrency`.
 *
 *  - `selectedCurrency`: The currently selected currency from Redux.
 *  - `shouldExpandSum`: Global expand-sum mode flag from Redux. Determines whether
 *    the entire ExpandSum component renders anything at all.
 *
 *  - `menuRef`: A reference to the LOCATION currency dropdown container.
 *
 * Derived Values:
 *  - `rowDensityClass`, `rowDensityHeight`: Layout utilities from `useRowDensity`
 *    controlling padding and height.
 *
 *  - `imgUrl`, `symbol`, `hasSpecialSymbol`: Parsed currency assets returned by
 *    `mapCCYAssets`, used to display icon + symbol in the LOCATION expand-sum button.
 *
 * Behavior:
 *
 * 1. **Excluded Geo Columns**
 *    - When expand-sum mode is active, geo columns (except LOCATION) should not
 *      display expanded values. These render an empty `FooterWrapper` with height
 *      aligned to row density.
 *
 * 2. **Merged LOCATION + CCY Columns**
 *    - When expand-sum mode is active, CCY column becomes visually merged into
 *      the LOCATION footer cell.
 *    - LOCATION footer shows:
 *        • A currency dropdown (`CurrencyList`)
 *        • A button showing the selected currency + icon
 *        • A toggle icon for expanding/collapsing the currency list
 *
 * 3. **Hidden CCY Column**
 *    - When expand-sum is active, CCY column is completely hidden (merged with LOCATION).
 *
 * 4. **Other Columns**
 *    - All non-geo, non-LOCATION, non-CCY columns render their aggregated
 *      footer value (`data`) with appropriate borders.
 *
 * 5. **Auto-close Menu**
 *    - The currency dropdown automatically closes when clicking outside or
 *      scrolling the container (via `useCloseOnScrollAndClickOutside`).
 *
 * Example:
 *  <ExpandSum
 *    column="AMOUNT"
 *    isLastColumn={false}
 *    isFirstColumnAfterCCY={false}
 *    className="visible_footer"
 *  />
 */

import React, { useRef, useState } from "react";
import { StatsTableColumn } from "../../../../../enum/TableColumn";
import { GeoColumns } from "../../../../../enum/GeoDivisionColumnName";
import {
  borderStyle,
  getTableClass,
  isGeoColumnExcludingLocation,
} from "../../../../../util/table-helper";
import upArrow from "../../../../../assets/img/svg/table/up-arrow.svg";
import Button from "../../Button";
import CurrencyList from "./CurrencyList/CurrencyList";
import { useCloseOnScrollAndClickOutside } from "../../../../../hook/useCloseOnScrollAndClickOutside";
import { useAppSelector } from "../../../../../hook/useAppSelector";
import { useViewPerCurrency } from "../../../../../hook/useViewPerCurrency";
import { useRowDensity } from "../../../../../hook/useRowDensity";
import { mapCCYAssets } from "../../../../../util/currency";
import FooterWrapper from "./FooterWrapper";

interface TableFooterProps {
  column: string;
  isLastColumn: boolean;
  isFirstColumnAfterCCY: boolean;
  className: string;
}

const ExpandSum: React.FC<TableFooterProps> = ({
  column,
  isLastColumn,
  isFirstColumnAfterCCY,
  className,
}) => {
  const data = useViewPerCurrency(column);
  const [shouldExpandCurrencies, setShouldExpandCurrencies] = useState(false);

  const selectedCurrency = useAppSelector(
    (state) => state.stats.selectedCurrency
  );
  const shouldExpandSum = useAppSelector((state) => state.data.shouldExpandSum);

  const menuRef = useRef(null);
  const { rowDensityClass, rowDensityHeight } = useRowDensity();

  const { imgUrl, symbol, hasSpecialSymbol } = mapCCYAssets(
    selectedCurrency ?? ""
  );

  useCloseOnScrollAndClickOutside(setShouldExpandCurrencies, menuRef);

  const handleShouldExpandCurrencyMenu = () => {
    setShouldExpandCurrencies((prev) => !prev);
  };

  // Skip excluded geo columns
  if (shouldExpandSum && isGeoColumnExcludingLocation(column)) {
    return (
      <FooterWrapper
        column={column}
        className={className}
        height={className === "hidden_footer" ? 0 : rowDensityHeight}
        children={undefined}
      />
    );
  }

  // Render merged cell for LOCATION (spanning LOCATION + CCY)
  if (shouldExpandSum && column === GeoColumns.LOCATION) {
    return (
      <FooterWrapper column={column} className={className} refProp={menuRef}>
        <div className={`table_row ${getTableClass(column, "footer_row")}`}>
          <div className={`table_cell ${rowDensityClass} location_column`}>
            {shouldExpandCurrencies && (
              <CurrencyList
                shouldExpandCurrencies={shouldExpandCurrencies}
                currencyMenuHandler={handleShouldExpandCurrencyMenu}
              />
            )}
            <Button
              className={`button expand_sum ${rowDensityClass}`}
              icon={imgUrl}
              iconRight={upArrow}
              onClick={handleShouldExpandCurrencyMenu}
            >
              {selectedCurrency} {hasSpecialSymbol && symbol}
            </Button>
          </div>
        </div>
      </FooterWrapper>
    );
  }

  // Skip CCY column (it’s merged with LOCATION)
  if (shouldExpandSum && column === StatsTableColumn.CCY) {
    return (
      <FooterWrapper
        column={column}
        className={className}
        height={className === "hidden_footer" ? 0 : rowDensityHeight}
        children={undefined}
      />
    );
  }

  // Default for other columns
  return (
    <>
      {shouldExpandSum && (
        <FooterWrapper column={column} className={className}>
          <div className={`table_row ${getTableClass(column, "footer_row")}`}>
            <div
              className={`table_cell ${rowDensityClass}`}
              style={borderStyle({
                isFull: true,
                isLastColumn,
                isFirstColumnAfterCCY,
              })}
            >
              {data}
            </div>
          </div>
        </FooterWrapper>
      )}
    </>
  );
};

export default ExpandSum;
